import { useState } from 'react'
import { useAIChat } from './hooks/useAIChat'
import { useScrollManagement } from './hooks/useScrollManagement'
import { useImagePaste } from './hooks/useImagePaste'
import { EditConfirmationOverlay } from './components/EditConfirmationOverlay'
import { MessageList } from './components/MessageList'
import { SelectedTextIndicator } from './components/SelectedTextIndicator'
import { ImagePreview } from './components/ImagePreview'
import { ChatInput } from './components/ChatInput'

/**
 * ChatInterface - Main orchestrator component for the AI chat functionality
 * 
 * Component Hierarchy:
 * ```
 * ChatInterface (this component)
 * ├── EditConfirmationOverlay      - Overlay for reviewing/applying AI-proposed edits
 * ├── MessageList                  - Scrollable container for all messages
 * │   └── MessageRenderer          - Renders individual messages based on role
 * │       ├── SystemMessage        - System notifications (confirmations, errors)
 * │       ├── UserMessage          - User messages with optional images
 * │       └── AssistantMessage     - AI responses
 * │           ├── MarkdownRenderer - Renders markdown content
 * │           └── MessageMetadata  - Edit proposals & referenced notes
 * └── Input Area
 *     ├── SelectedTextIndicator    - Shows selected text from editor
 *     ├── ImagePreview             - Preview of pasted images
 *     └── ChatInput                - Text input with send button
 * ```
 * 
 * Data Flow:
 * 1. Context: Uses `useAIChat` hook to access ChatContextProvider state
 * 2. Input: User types message → handleSubmit → sendMessage (from context)
 * 3. Images: Paste event → useImagePaste hook → base64 conversion → sent with message
 * 4. Messages: Received from context → MessageList → individual MessageRenderers
 * 5. Edits: AI proposes edit → EditConfirmationOverlay → user confirms → applyEdit
 * 
 * Custom Hooks:
 * - useAIChat: Accesses chat context (messages, loading states, functions)
 * - useScrollManagement: Auto-scrolls to bottom on new messages
 * - useImagePaste: Handles clipboard paste events for images
 */
export function ChatInterface() {
  const {
    messages,
    sendMessage,
    isLoading,
    applyEdit,
    showEditConfirmation,
    setShowEditConfirmation,
    pendingEdit,
    setPendingEdit,
    selectedText,
    selectedLineCount,
    isSearching,
    webSearchStatus,
  } = useAIChat()
  
  const [input, setInput] = useState('')
  
  // Use custom hooks for scroll management and image handling
  const { messagesEndRef, messagesContainerRef, handleScroll } = useScrollManagement(messages, isLoading)
  const { pastedImages, handlePaste, removeImage, clearImages } = useImagePaste()

  const handleSubmit = () => {
    if ((input.trim() || pastedImages.length > 0) && !isLoading) {
      sendMessage(
        input.trim() || 'Here is an image:',
        pastedImages.length > 0 ? pastedImages : undefined
      )
      setInput('')
      clearImages()
    }
  }

  const handleEditConfirm = () => {
    if (pendingEdit) {
      applyEdit(pendingEdit)
      setShowEditConfirmation(false)
      setPendingEdit(undefined)
    }
  }

  const handleEditCancel = () => {
    setShowEditConfirmation(false)
    setPendingEdit(undefined)
  }

  const handlePasteWrapper = (e: React.ClipboardEvent) => {
    void handlePaste(e)
  }

  return (
    <>
      {/* Edit Confirmation Overlay */}
      <EditConfirmationOverlay
        show={showEditConfirmation}
        pendingEdit={pendingEdit}
        onConfirm={handleEditConfirm}
        onCancel={handleEditCancel}
      />

      <div className='flex h-full w-full flex-col bg-amber-50 dark:bg-amber-950/15'>
        {/* Messages */}
        <MessageList
          ref={messagesContainerRef}
          messages={messages}
          isLoading={isLoading}
          isSearching={isSearching}
          webSearchStatus={webSearchStatus}
          onScroll={handleScroll}
          messagesEndRef={messagesEndRef}
        />

        {/* Input Area */}
        <div className='p-4'>
          {/* Selected text indicator */}
          <SelectedTextIndicator 
            selectedText={selectedText} 
            selectedLineCount={selectedLineCount} 
          />

          {/* Pasted images preview */}
          <ImagePreview 
            images={pastedImages} 
            onRemove={removeImage} 
          />

          {/* Chat input */}
          <ChatInput
            value={input}
            onChange={setInput}
            onSubmit={handleSubmit}
            onPaste={handlePasteWrapper}
            isLoading={isLoading}
          />
        </div>
      </div>
    </>
  )
}